import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Breadcrumb, Tabs } from '@components/base/navigation';
import { HStack, VStack, Card, Divider } from '@components/base/layout';
import { Alert, Spinner } from '@components/base/feedback';
import { tenantsService } from '../../api/tenantsService';
import type { Tenant } from '../../api/tenantsService';
import { TenantUsersTab } from './TenantUsersTab';
import { TenantModulesTab } from './TenantModulesTab';
import { TenantRegistriesTab } from './TenantRegistriesTab';

function TenantOverviewTab({ tenant }: Readonly<{ tenant: Tenant }>) {
  return (
    <VStack gap={6}>
      <Card>
        <Card.Header title="General Information" />
        <Card.Content>
          <VStack gap={4}>
            <HStack gap={8}>
              <VStack gap={1}>
                <Text size="xs" color="muted" weight="medium">
                  Name
                </Text>
                <Text>{tenant.name}</Text>
              </VStack>
              <VStack gap={1}>
                <Text size="xs" color="muted" weight="medium">
                  Slug
                </Text>
                <Text>{tenant.slug}</Text>
              </VStack>
              <VStack gap={1}>
                <Text size="xs" color="muted" weight="medium">
                  Domain
                </Text>
                <Text>{tenant.domain ?? '--'}</Text>
              </VStack>
              <VStack gap={1}>
                <Text size="xs" color="muted" weight="medium">
                  Status
                </Text>
                <Badge
                  variant={tenant.is_active ? 'success' : 'default'}
                  size="sm"
                >
                  {tenant.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </VStack>
            </HStack>

            <Divider />

            <HStack gap={8}>
              <VStack gap={1}>
                <Text size="xs" color="muted" weight="medium">
                  Users
                </Text>
                <Text>{tenant.user_count}</Text>
              </VStack>
              <VStack gap={1}>
                <Text size="xs" color="muted" weight="medium">
                  Created At
                </Text>
                <Text size="sm">
                  {new Date(tenant.created_at).toLocaleString()}
                </Text>
              </VStack>
              <VStack gap={1}>
                <Text size="xs" color="muted" weight="medium">
                  Updated At
                </Text>
                <Text size="sm">
                  {new Date(tenant.updated_at).toLocaleString()}
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </Card.Content>
      </Card>

      {tenant.settings && Object.keys(tenant.settings).length > 0 && (
        <Card>
          <Card.Header title="Settings" />
          <Card.Content>
            <Text
              as="p"
              size="sm"
              className="whitespace-pre-wrap font-mono bg-[#F5F4F2] p-3 rounded"
            >
              {JSON.stringify(tenant.settings, null, 2)}
            </Text>
          </Card.Content>
        </Card>
      )}
    </VStack>
  );
}

export function TenantDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const {
    data: tenantData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'tenants', slug],
    queryFn: () => tenantsService.get(slug!),
    enabled: slug !== undefined,
  });

  const tenant = tenantData?.data;

  // ---- Render: Loading ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-16">
        <Spinner size="lg" />
        <Text color="muted">Loading tenant...</Text>
      </VStack>
    );
  }

  // ---- Render: Error ----

  if (error) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/tenants">Tenants</Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Error</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Failed to load tenant">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred while loading the tenant.'}
        </Alert>
      </VStack>
    );
  }

  if (!tenant) {
    return (
      <Alert variant="error" title="Tenant not found">
        The requested tenant could not be found.
      </Alert>
    );
  }

  // ---- Render: Main ----

  return (
    <VStack gap={6}>
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/tenants">Tenants</Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{tenant.name}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Tenant Header */}
      <HStack justify="between" align="center">
        <VStack gap={1}>
          <HStack gap={3} align="center">
            <Text as="h1" size="2xl" weight="bold">
              {tenant.name}
            </Text>
            <Badge
              variant={tenant.is_active ? 'success' : 'default'}
              size="sm"
            >
              {tenant.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </HStack>
          <Text size="sm" color="muted">
            {tenant.slug}
            {tenant.domain ? ` - ${tenant.domain}` : ''}
          </Text>
        </VStack>
        <Button
          variant="secondary"
          leftIcon={<Icon icon={Pencil} size="sm" />}
          onClick={() => navigate(`/admin/tenants/${tenant.slug}/edit`)}
        >
          Edit Tenant
        </Button>
      </HStack>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="users">Users</Tabs.Tab>
          <Tabs.Tab value="modules">Modules</Tabs.Tab>
          <Tabs.Tab value="registries">Registries</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          <TenantOverviewTab tenant={tenant} />
        </Tabs.Panel>

        <Tabs.Panel value="users">
          <TenantUsersTab tenant={tenant} />
        </Tabs.Panel>

        <Tabs.Panel value="modules">
          <TenantModulesTab tenantId={tenant.slug} />
        </Tabs.Panel>

        <Tabs.Panel value="registries">
          <TenantRegistriesTab tenantId={tenant.slug} />
        </Tabs.Panel>
      </Tabs>
    </VStack>
  );
}
